import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home, ChevronRight, ChevronDown, Download, CalendarDays, AlertCircle } from 'lucide-react';
import { fetchAcademicCalendar } from '../utils/api';

const CATEGORIES = {
  Registrasi: { dot: 'bg-blue-500', badge: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700' },
  Perkuliahan: { dot: 'bg-green-500', badge: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border-green-200 dark:border-green-700' },
  Ujian: { dot: 'bg-red-500', badge: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-700' },
  Libur: { dot: 'bg-orange-400', badge: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-700' },
  Wisuda: { dot: 'bg-purple-500', badge: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-700' },
  Lainnya: { dot: 'bg-gray-400', badge: 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600' },
};

const FALLBACK_SEMESTERS = {
  'ganjil-2024': {
    label: 'Ganjil 2024/2025',
    fullLabel: 'Semester Ganjil Tahun Akademik 2024/2025',
    events: [
      { date: '26 Agt – 6 Sep 2024', title: 'Registrasi Ulang Mahasiswa Lama', category: 'Registrasi', desc: 'Pembayaran biaya pendidikan (SPP/UKT) dan registrasi akademik semester ganjil 2024/2025 melalui SIAKAD.' },
      { date: '9 – 13 Sep 2024', title: 'Orientasi Mahasiswa Baru (OSMB)', category: 'Lainnya', desc: 'Pengenalan kehidupan kampus, program studi, dan organisasi kemahasiswaan bagi mahasiswa baru angkatan 2024.' },
      { date: '16 – 20 Sep 2024', title: 'Pengisian KRS (Kartu Rencana Studi)', category: 'Registrasi', desc: 'Pengisian rencana studi dan konsultasi akademik dengan dosen wali melalui sistem SIAKAD.' },
      { date: '23 Sep – 20 Des 2024', title: 'Pelaksanaan Perkuliahan', category: 'Perkuliahan', desc: 'Perkuliahan reguler tatap muka (offline) dan/atau daring sesuai jadwal yang telah ditetapkan.' },
      { date: '28 Okt 2024', title: 'Libur Hari Sumpah Pemuda', category: 'Libur', desc: 'Libur Nasional memperingati Hari Sumpah Pemuda. Tidak ada kegiatan perkuliahan.' },
      { date: '10 Nov 2024', title: 'Libur Hari Pahlawan Nasional', category: 'Libur', desc: 'Libur Nasional Hari Pahlawan. Tidak ada kegiatan perkuliahan.' },
      { date: '25 – 29 Nov 2024', title: 'Ujian Tengah Semester (UTS)', category: 'Ujian', desc: 'Evaluasi pembelajaran tengah semester untuk seluruh mata kuliah. Mahasiswa wajib hadir minimal 75% perkuliahan.' },
      { date: '23 Des 2024 – 1 Jan 2025', title: 'Libur Hari Raya Natal & Tahun Baru', category: 'Libur', desc: 'Libur Hari Raya Natal dan Tahun Baru. Kampus libur dari tanggal 23 Desember 2024 hingga 1 Januari 2025.' },
      { date: '6 – 17 Jan 2025', title: 'Ujian Akhir Semester (UAS)', category: 'Ujian', desc: 'Evaluasi akhir semester ganjil 2024/2025 untuk seluruh mata kuliah. Jadwal ujian diumumkan H-7.' },
      { date: '20 – 31 Jan 2025', title: 'Pengumuman Nilai & Input KHS', category: 'Lainnya', desc: 'Periode input nilai akhir oleh dosen, verifikasi, dan penerbitan Kartu Hasil Studi (KHS) semester ganjil.' },
      { date: '15 Feb 2025', title: 'Wisuda Semester Ganjil 2024/2025', category: 'Wisuda', desc: 'Upacara wisuda STIKES Harapan Ibu Jambi bagi lulusan yang telah memenuhi syarat akademik dan administrasi.' },
    ],
  },
  'genap-2025': {
    label: 'Genap 2024/2025',
    fullLabel: 'Semester Genap Tahun Akademik 2024/2025',
    events: [
      { date: '3 – 14 Feb 2025', title: 'Registrasi Ulang Mahasiswa', category: 'Registrasi', desc: 'Pembayaran biaya pendidikan dan registrasi akademik semester genap 2024/2025 melalui SIAKAD.' },
      { date: '17 – 21 Feb 2025', title: 'Pengisian KRS Semester Genap', category: 'Registrasi', desc: 'Pengisian rencana studi dan konsultasi dengan dosen wali untuk semester genap.' },
      { date: '24 Feb – 13 Jun 2025', title: 'Pelaksanaan Perkuliahan', category: 'Perkuliahan', desc: 'Perkuliahan reguler tatap muka dan daring sesuai jadwal semester genap 2024/2025.' },
      { date: '18 Mar – 2 Apr 2025', title: 'Libur Idul Fitri 1446 H', category: 'Libur', desc: 'Libur Hari Raya Idul Fitri 1446 H. Cuti bersama berlaku mulai 18 Maret hingga 2 April 2025.' },
      { date: '3 Apr 2025', title: 'Libur Hari Raya Nyepi', category: 'Libur', desc: 'Libur Nasional Hari Raya Nyepi (Tahun Baru Saka 1947).' },
      { date: '5 – 9 Mei 2025', title: 'Ujian Tengah Semester (UTS)', category: 'Ujian', desc: 'Evaluasi pembelajaran tengah semester genap 2024/2025 untuk seluruh mata kuliah aktif.' },
      { date: '1 Jun 2025', title: 'Libur Hari Lahir Pancasila', category: 'Libur', desc: 'Libur Nasional Hari Lahir Pancasila. Tidak ada kegiatan perkuliahan.' },
      { date: '16 – 27 Jun 2025', title: 'Ujian Akhir Semester (UAS)', category: 'Ujian', desc: 'Evaluasi akhir semester genap 2024/2025. Jadwal ujian diumumkan minimal H-7 sebelum pelaksanaan.' },
      { date: '30 Jun – 11 Jul 2025', title: 'Pengumuman Nilai & Input KHS', category: 'Lainnya', desc: 'Input nilai akhir oleh dosen, verifikasi oleh ketua program studi, dan penerbitan KHS semester genap.' },
      { date: '21 Jul – 29 Ags 2025', title: 'Libur Antarsemester', category: 'Libur', desc: 'Libur antarsemester sebelum dimulainya semester ganjil tahun akademik 2025/2026.' },
      { date: '30 Ags 2025', title: 'Wisuda Semester Genap 2024/2025', category: 'Wisuda', desc: 'Upacara wisuda STIKES HI Jambi bagi lulusan yang telah memenuhi semua persyaratan akademik dan administrasi.' },
    ],
  },
};

const TAB_GAP = 8;
const tabClass = 'whitespace-nowrap rounded-xl px-4 py-2.5 text-sm font-medium';

function semesterChronology(semester) {
  const yearSource = `${semester.label || ''} ${semester.fullLabel || ''} ${semester.slug || ''}`;
  const years = [...yearSource.matchAll(/\b(?:19|20)\d{2}\b/g)].map((match) => Number(match[0]));
  const termSource = `${semester.label || ''} ${semester.slug || ''}`.toLowerCase();
  const term = /\bgenap\b/.test(termSource) ? 2 : /\bganjil\b/.test(termSource) ? 1 : 0;
  return { year: years.length ? Math.max(...years) : 0, term };
}

function newestFirst(semesters) {
  return [...semesters].sort((left, right) => {
    const leftChronology = semesterChronology(left);
    const rightChronology = semesterChronology(right);
    return rightChronology.year - leftChronology.year
      || rightChronology.term - leftChronology.term
      || (Number(right.id) || 0) - (Number(left.id) || 0);
  });
}

function academicYearLabel(semester) {
  const source = `${semester?.label || ''} ${semester?.fullLabel || ''}`;
  const match = source.match(/\b((?:19|20)\d{2})\s*[/-]\s*((?:19|20)\d{2})\b/);
  return match ? `${match[1]}/${match[2]}` : semester?.label || 'terkini';
}

function SemesterSelector({ semesters, activeSlug, onSelect }) {
  const entries = Object.entries(semesters);
  const signature = entries.map(([key, semester]) => `${key}:${semester.label}`).join('|');
  const containerRef = useRef(null);
  const measurementRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(entries.length);

  useEffect(() => {
    const container = containerRef.current;
    const measurement = measurementRef.current;
    if (!container || !measurement) return undefined;

    const calculateVisibleCount = () => {
      const tabWidths = [...measurement.querySelectorAll('[data-measure-tab]')]
        .map((element) => element.getBoundingClientRect().width);
      const moreWidth = measurement.querySelector('[data-measure-more]')?.getBoundingClientRect().width || 0;
      const availableWidth = container.getBoundingClientRect().width;
      let usedWidth = 0;
      let nextCount = 0;

      tabWidths.forEach((width, index) => {
        if (nextCount !== index) return;
        const gapBefore = nextCount ? TAB_GAP : 0;
        const needsMoreButton = index < tabWidths.length - 1;
        const reservedWidth = needsMoreButton ? TAB_GAP + moreWidth : 0;
        if (usedWidth + gapBefore + width + reservedWidth <= availableWidth || nextCount === 0) {
          usedWidth += gapBefore + width;
          nextCount += 1;
        }
      });

      setVisibleCount((current) => current === nextCount ? current : nextCount);
    };

    calculateVisibleCount();
    const observer = new ResizeObserver(calculateVisibleCount);
    observer.observe(container);
    return () => observer.disconnect();
  }, [signature]);

  const visibleEntries = entries.slice(0, visibleCount);
  const overflowEntries = entries.slice(visibleCount);
  const overflowActive = overflowEntries.some(([key]) => key === activeSlug);

  return (
    <div ref={containerRef} className="relative min-w-0 flex-1">
      <div ref={measurementRef} aria-hidden="true" className="pointer-events-none invisible absolute left-0 top-0 flex w-max gap-2">
        {entries.map(([key, semester]) => <span key={key} data-measure-tab className={tabClass}>{semester.label}</span>)}
        <span data-measure-more className={`${tabClass} inline-flex items-center gap-1.5`}>Lainnya <ChevronDown size={14} /></span>
      </div>

      <div className="flex gap-2 overflow-hidden">
        {visibleEntries.map(([key, semester]) => (
          <button
            type="button"
            key={key}
            data-testid={`sem-btn-${key}`}
            onClick={() => onSelect(key)}
            className={`${tabClass} transition-all duration-200 ${
              activeSlug === key
                ? 'bg-[#7B2FBE] text-white shadow-lg shadow-purple-500/20'
                : 'border border-gray-200 bg-white text-gray-600 hover:border-[#7B2FBE] dark:border-gray-700 dark:bg-[#1A182E] dark:text-gray-300'
            }`}
          >
            {semester.label}
          </button>
        ))}

        {overflowEntries.length > 0 && (
          <label className={`relative inline-flex shrink-0 cursor-pointer items-center gap-1.5 ${tabClass} transition-all ${
            overflowActive
              ? 'bg-[#7B2FBE] text-white shadow-lg shadow-purple-500/20'
              : 'border border-gray-200 bg-white text-gray-600 hover:border-[#7B2FBE] dark:border-gray-700 dark:bg-[#1A182E] dark:text-gray-300'
          }`}>
            Lainnya <ChevronDown size={14} />
            <select
              aria-label="Pilih semester lainnya"
              value=""
              onChange={(event) => event.target.value && onSelect(event.target.value)}
              className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
            >
              <option value="" disabled>Pilih semester</option>
              {overflowEntries.map(([key, semester]) => <option key={key} value={key}>{semester.label}</option>)}
            </select>
          </label>
        )}
      </div>
    </div>
  );
}

const KalenderAkademik = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [semesters, setSemesters] = useState(() => Object.fromEntries(
    newestFirst(Object.entries(FALLBACK_SEMESTERS).map(([slugKey, semester]) => ({ ...semester, slug: slugKey })))
      .map((semester) => [semester.slug, semester]),
  ));
  const [calendarResolved, setCalendarResolved] = useState(false);
  const firstSemesterSlug = Object.keys(semesters)[0];
  const activeSem = slug && semesters[slug] ? slug : firstSemesterSlug;
  const sem = semesters[activeSem] || Object.values(semesters)[0];

  useEffect(() => {
    let active = true;
    fetchAcademicCalendar()
      .then((data) => {
        if (!active || !Array.isArray(data) || !data.length) return;
        const next = Object.fromEntries(newestFirst(data).map((semester) => [
          semester.slug,
          {
            ...semester,
            events: (semester.events || []).map((event) => ({
              ...event,
              date: event.dateLabel,
              desc: event.description,
            })),
          },
        ]));
        setSemesters(next);
      })
      .catch((error) => console.warn('Kalender akademik memakai data fallback:', error.message))
      .finally(() => {
        if (active) setCalendarResolved(true);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!calendarResolved || !firstSemesterSlug) return;
    if (!slug || !semesters[slug]) {
      navigate(`/kalender/${firstSemesterSlug}`, { replace: true });
    }
  }, [calendarResolved, firstSemesterSlug, navigate, semesters, slug]);

  return (
    <div data-testid="kalender-page">
      <div className="page-hero pt-[70px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 relative z-10">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <div className="flex items-center gap-2 text-white/70 text-sm mb-4 flex-wrap">
              <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
                <Home size={14} /> Beranda
              </Link>
              <ChevronRight size={14} />
              <span className="text-white/70">Akademik</span>
              <ChevronRight size={14} />
              <span className="text-white">Kalender Akademik</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white font-heading mb-4">Kalender Akademik</h1>
            <p className="text-white/80 max-w-lg text-base">
              Jadwal resmi kegiatan akademik STIKES Harapan Ibu Jambi Tahun Akademik {academicYearLabel(sem)}.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="bg-[#F8F7FF] dark:bg-[#121020]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

          {/* Semester Selector + Download */}
          <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between mb-8">
            <SemesterSelector semesters={semesters} activeSlug={activeSem} onSelect={(key) => navigate(`/kalender/${key}`)} />
            {sem.pdfUrl ? (
              <a
                href={sem.pdfUrl}
                data-testid="download-kalender-btn"
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium border border-[#7B2FBE] text-[#7B2FBE] dark:border-purple-500 dark:text-purple-400 hover:bg-[#7B2FBE] hover:text-white transition-all"
              >
                <Download size={14} /> Unduh PDF
              </a>
            ) : (
              <button
                type="button"
                disabled
                title="PDF kalender untuk semester ini belum tersedia"
                data-testid="download-kalender-btn"
                className="flex cursor-not-allowed items-center gap-2 rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-400 opacity-70 dark:border-gray-700 dark:text-gray-500"
              >
                <Download size={14} /> PDF belum tersedia
              </button>
            )}
          </div>

          {/* Active Semester Label */}
          <div className="bg-white dark:bg-[#1A182E] rounded-2xl p-5 mb-8 border border-gray-100 dark:border-gray-800 shadow-sm flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#7B2FBE] to-[#9333EA] flex items-center justify-center text-white">
              <CalendarDays size={22} />
            </div>
            <div>
              <p className="text-xs text-[#7B2FBE] font-semibold uppercase tracking-widest mb-0.5">Sedang Ditampilkan</p>
              <h2 className="text-lg font-bold text-[#1A1A2E] dark:text-white font-heading">{sem.fullLabel}</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{sem.events.length} kegiatan terjadwal</p>
            </div>
          </div>

          {/* Legend */}
          <div className="flex flex-wrap gap-x-5 gap-y-2 mb-8">
            {Object.entries(CATEGORIES).map(([cat, val]) => (
              <span key={cat} className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${val.dot}`} />
                {cat}
              </span>
            ))}
          </div>

          {/* Timeline */}
          <div className="relative">
            <div className="absolute left-[88px] top-0 bottom-0 w-px bg-gradient-to-b from-purple-200 via-gray-200 to-transparent dark:from-purple-800/50 dark:via-gray-700" />

            <div className="space-y-5">
              {sem.events.map((ev, i) => {
                const cat = CATEGORIES[ev.category] || CATEGORIES.Lainnya;
                return (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    data-testid={`event-item-${i}`}
                    className="flex gap-4 items-start"
                  >
                    {/* Date */}
                    <div className="w-[88px] flex-shrink-0 text-right pt-3">
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium leading-snug">{ev.date}</span>
                    </div>
                    {/* Dot */}
                    <div className={`w-3.5 h-3.5 rounded-full mt-3.5 flex-shrink-0 relative z-10 ring-2 ring-[#F8F7FF] dark:ring-[#0A0A0F] ${cat.dot}`} />
                    {/* Card */}
                    <div className="flex-1 bg-white dark:bg-[#1A182E] rounded-xl border border-gray-100 dark:border-gray-800 p-4 shadow-sm hover:shadow-md transition-all hover:-translate-y-0.5 duration-200">
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <h4 className="font-semibold text-[#1A1A2E] dark:text-white text-sm font-heading leading-tight">{ev.title}</h4>
                        <span className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium border ${cat.badge}`}>
                          {ev.category}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{ev.desc}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Info Banner */}
          <div className="mt-12 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800/40 rounded-2xl p-5 flex items-start gap-3">
            <AlertCircle size={18} className="text-[#7B2FBE] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-[#7B2FBE] dark:text-purple-300 mb-1">Catatan Penting</p>
              <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                Kalender akademik ini dapat berubah sewaktu-waktu menyesuaikan kebijakan institusi dan ketentuan pemerintah.
                Perubahan akan diumumkan melalui papan pengumuman, website resmi, dan grup resmi mahasiswa.
                Untuk informasi lebih lanjut, hubungi Bagian Akademik STIKES HI Jambi.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KalenderAkademik;
